import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useSlotRecipe, RadioGroup} from '@chakra-ui/react3'
import {colorSwatchesRecipe} from './index.recipe'
import {useProductVariantsOrderable} from '../../../hooks/use-product'
import {useColorVariation} from '../../../hooks'

export const ColorSwatchItem = ({swatch, isOrderable = swatch?.orderable, size, ...rest}) => {
    const recipe = useSlotRecipe({recipe: colorSwatchesRecipe})
    const styles = useMemo(() => recipe({size}), [recipe, size])
    return (
        <RadioGroup.Item
            key={swatch.value}
            value={swatch.productId}
            disabled={!isOrderable}
            aria-label={swatch.name}
            {...styles.swatch}
            {...rest}
        >
            <RadioGroup.ItemHiddenInput />
            <Box {...styles.swatchInner}>
                {swatch.image ? (
                    <Box
                        backgroundRepeat="no-repeat"
                        backgroundSize="cover"
                        backgroundColor={swatch.name}
                        backgroundImage={`url("${encodeURI(
                            swatch.image.link ?? swatch.image.disBaseLink
                        )}")`}
                        {...styles.swatchImage}
                    />
                ) : (
                    <Text {...styles.variationText}>{swatch.name.charAt(0)}</Text>
                )}
            </Box>
        </RadioGroup.Item>
    )
}
ColorSwatchItem.propTypes = {
    isOrderable: PropTypes.bool,
    swatch: PropTypes.shape({
        productId: PropTypes.string,
        name: PropTypes.string,
        value: PropTypes.string,
        image: PropTypes.shape({
            link: PropTypes.string,
            disBaseLink: PropTypes.string
        }),
        orderable: PropTypes.bool
    }),
    size: PropTypes.string
}

/**
 * @param {{
 *   selector: {
 *     id: string,
 *     name: string,
 *     setProductId: string,
 *     variationAttributes: Array<Object>
 *   },
 *   selectedProductId: string,
 *   orderableByProductId: Object
 * }} props
 */
const ColorSwatchGroup = ({selector, selectedProductId, orderableByProductId, onChange}) => {
    const colorVariation = useColorVariation(selector, true)

    const onValueChange = useCallback(
        ({value: productId}) => {
            onChange?.(colorVariation.values.find((v) => v.productId === productId) ?? {productId})
        },
        [onChange, colorVariation.values]
    )

    if (!colorVariation?.values?.length) return null

    return (
        <Box as="fieldset" border="none" padding="0" margin="0">
            {!!selector.name && (
                <Text as="legend" color="text.tertiary">
                    {selector.c_masterSubType ? selector.c_masterSubType : colorVariation.name}
                </Text>
            )}
            <RadioGroup.Root
                name={`${selector.setProductId}-${selector.id}`}
                value={selectedProductId}
                onValueChange={onValueChange}
                display="flex"
                flexWrap="wrap"
                gap="spacing-14"
            >
                {colorVariation.values.map((swatch) => (
                    <ColorSwatchItem
                        key={swatch.value}
                        size="26"
                        swatch={swatch}
                        isOrderable={
                            (orderableByProductId?.[swatch.productId] ?? swatch.orderable) !== false
                        }
                    />
                ))}
            </RadioGroup.Root>
        </Box>
    )
}

ColorSwatchGroup.propTypes = {
    selector: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        setProductId: PropTypes.string,
        variationAttributes: PropTypes.array,
        c_masterSubType: PropTypes.string
    }),
    selectedProductId: PropTypes.string,
    orderableByProductId: PropTypes.object,
    onChange: PropTypes.func
}

/**
 * @param {Object} props
 * @param {Array<{
 *   id: string,
 *   name: string,
 *   setProductId: string,
 *   variationAttributes: Array<Object>,
 *   variants: Array<{
 *     productId: string,
 *     name: string,
 *     variationValues: {color: string}
 *   }>,
 *   imageGroups: Array<Object>
 * }>} props.selectors - Color selectors from the Configurator API response (`product.selectors`).
 *   NOTE: This shape is specific to the Configurator API and may differ from other product APIs.
 *   Each selector is enriched by useConfigurationItem with `setProductId` and `variationAttributes`.
 * @param {string} props.selectedProductId
 */
const ColorSwatches = ({selectors, selectedProductId, onChange}) => {
    const selectorIds = useMemo(() => selectors?.map((s) => s.id) ?? [], [selectors])
    const {data: orderableByProductId} = useProductVariantsOrderable(selectorIds)

    return (
        <Flex flexDirection="column" gap="spacing-24">
            {selectors?.map((selector) => (
                <ColorSwatchGroup
                    key={selector.id}
                    selector={selector}
                    selectedProductId={selectedProductId}
                    orderableByProductId={orderableByProductId}
                    onChange={onChange}
                />
            ))}
        </Flex>
    )
}

ColorSwatches.propTypes = {
    selectors: PropTypes.array,
    selectedProductId: PropTypes.string,
    onChange: PropTypes.func
}

export default ColorSwatches
